import React from 'react';
import { createPortal } from 'react-dom';

import './stylesheet.css';

/**
 * A small modal offering a list of choices, plus cancel.
 *
 * Used for the daystart prompt (fast / slow / cancel) and for picking a snippet to
 * insert. Deliberately not routed through base.activatePopup(): those popups are
 * tied to the Org file editing lifecycle, while these two are plain questions that
 * can also be asked from the file browser and the dashboard.
 *
 * `choices` is [{ key, label, description, icon }]. Cancelling — the backdrop, the
 * Escape-sized cancel button — always resolves through onCancel, so a caller never
 * has to guess whether the user picked something.
 *
 * INFO: Rendered into document.body through a portal, because its callers live
 * inside <ActionDrawer>, whose container sets `pointer-events: none` so that clicks
 * fall through to the file behind the floating buttons. Inherited, that made the
 * whole modal unclickable. The portal also escapes the drawer's flex layout, its
 * `white-space: nowrap` and its z-index: 2 stacking context.
 */
const ChoiceModal = ({ title, description, choices, onChoose, onCancel, isBusy, busyMessage }) =>
  createPortal(
    <div className="choice-modal__backdrop" onClick={isBusy ? undefined : onCancel}>
      <div
        className="choice-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="choice-modal__title">{title}</div>
        {!!description && <div className="choice-modal__description">{description}</div>}

        {isBusy ? (
          <div className="choice-modal__busy">
            <i className="fas fa-spinner fa-spin" /> {busyMessage || 'Working...'}
          </div>
        ) : (
          <>
            <div className="choice-modal__choices">
              {(choices || []).map((choice) => (
                <button
                  key={choice.key}
                  className="choice-modal__choice"
                  onClick={() => onChoose(choice.key)}
                >
                  {!!choice.icon && (
                    <i className={`fas fa-${choice.icon} choice-modal__choice__icon`} />
                  )}
                  <span className="choice-modal__choice__text">
                    <span className="choice-modal__choice__label">{choice.label}</span>
                    {!!choice.description && (
                      <span className="choice-modal__choice__description">
                        {choice.description}
                      </span>
                    )}
                  </span>
                </button>
              ))}
            </div>

            <button className="choice-modal__cancel" onClick={onCancel}>
              Cancel
            </button>
          </>
        )}
      </div>
    </div>,
    document.body
  );

export default ChoiceModal;
